import { useCallback, useEffect, useMemo, useState } from 'react';
import { liveQuery } from 'dexie';
import { statusAfterSurvey } from '@nu/shared';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';
import { db, cacheGet, cachePut } from '../../offline/db.js';

// Network Plus surveys, as this device knows them: the last list from the
// server (kept for no signal) with this device's own changes still waiting
// in the outbox laid on top, so a survey done in a dead zone shows at once.
// Reloads every 30 seconds while on screen, and when the outbox sends.
const KEY = 'surveys';

export function useSurveys() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle');
  const [waiting, setWaiting] = useState([]);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const d = await api('/surveys');
      setData(d);
      setStatus('ok');
      cachePut(KEY, d).catch(() => {});
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    cacheGet(KEY).then((d) => { if (d) setData((x) => x || d); }, () => {});
    load();
    const off = outbox.subscribe(load);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    const every = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30_000);
    const sub = liveQuery(() => db.outbox.toArray()).subscribe({
      next: (all) => setWaiting(all.filter((m) => m.type.startsWith('survey.'))),
    });
    return () => { off(); clearInterval(every); document.removeEventListener('visibilitychange', onVis); sub.unsubscribe(); };
  }, [load]);

  const jobs = useMemo(() => overlay(data?.jobs || [], waiting, data), [data, waiting]);
  const refused = waiting.filter((m) => m.status === 'rejected');
  return { data, jobs, status, refused, reload: load };
}

// This device's changes not yet on the server, laid over the list.
function overlay(jobs, waiting, data) {
  const pending = waiting.filter((m) => m.status === 'pending');
  if (!pending.length) return jobs;
  const byId = new Map(jobs.map((j) => [j.id, { ...j }]));
  const people = new Map((data?.surveyors || []).map((p) => [p.id, p.name]));
  const gangs = new Map((data?.gangs || []).map((g) => [g.teamId, g]));
  for (const m of pending) {
    const p = m.payload || {};
    const ids = p.jobIds || [p.jobId];
    for (const id of ids) {
      const j = byId.get(id);
      if (!j) continue;
      j.sending = true;
      if (m.type === 'survey.submit') {
        Object.assign(j, {
          presiteStatus: p.presiteStatus, presiteNotes: p.presiteNotes, surfaceType: p.surfaceType, mstLocation: p.mstLocation,
          permitConditions: p.permitConditions, permitLength: p.permitLength, plannedDate: p.plannedDate || null, details: p.details || j.details,
          jobStatus: statusAfterSurvey(j.jobStatus, p.plannedDate), surveyedAt: p.at, surveyedByName: p.byName || j.surveyedByName,
        });
        if ('teamId' in p && p.teamId !== j.teamId) {
          const g = gangs.get(p.teamId);
          Object.assign(j, { teamId: p.teamId, teamName: g?.teamName || null, gangName: g?.gangName || null, teamSetByHand: true });
        }
      } else if (m.type === 'survey.assign') {
        const on = p.surveyorIds || (p.surveyorId ? [p.surveyorId] : []);
        Object.assign(j, { surveyorIds: on, surveyorId: on[0] || null, surveyorName: on.length ? on.map((id) => people.get(id) || '').join(', ') : null });
      } else if (m.type === 'survey.pickup') {
        Object.assign(j, { surveyorIds: [p.meId], surveyorId: p.meId, surveyorName: p.byName });
      } else if (m.type === 'survey.release') {
        const left = (j.surveyorIds || []).filter((id) => id !== p.meId);
        Object.assign(j, { surveyorIds: left, surveyorId: left[0] || null, surveyorName: left.length ? left.map((id) => people.get(id) || '').join(', ') : null });
      } else if (m.type === 'survey.team') {
        const g = gangs.get(p.teamId);
        Object.assign(j, { teamId: p.teamId, teamName: g?.teamName || null, gangName: g?.gangName || null, teamSetByHand: true });
      } else if (m.type === 'survey.status') {
        j.jobStatus = p.status;
      }
    }
  }
  return jobs.map((j) => byId.get(j.id));
}

// The payload carries a few extras (byName, meId) only for the overlay above;
// the server ignores them.
export const sendSurvey = (me, job, values, where) => outbox.enqueue('survey.submit', {
  jobId: job.id,
  at: new Date().toISOString(),
  byName: me.name,
  ...values,
  plannedDate: values.plannedDate || null,
  where: where || null,
});
// surveyorIds: everyone who's to survey them ([] = nobody).
export const assignSurveyor = (jobIds, surveyorIds) => outbox.enqueue('survey.assign', { jobIds, surveyorIds });
export const pickUpJob = (me, job) => outbox.enqueue('survey.pickup', { jobId: job.id, meId: me.id, byName: me.name });
export const releaseJob = (me, job) => outbox.enqueue('survey.release', { jobId: job.id, meId: me.id });
export const setGang = (jobIds, teamId) => outbox.enqueue('survey.team', { jobIds, teamId: teamId || null });
export const setJobStatus = (job, status) => outbox.enqueue('survey.status', { jobId: job.id, status });
export const dismiss = (entry) => outbox.discard(entry.id);

// One colour per Job Status (styles.css .sv-*), on the board and in the app.
export const STATUS_CLASS = {
  'To Presite': 'sv-to-presite',
  Presited: 'sv-presited',
  Planned: 'sv-planned',
  Completed: 'sv-completed',
};

// The survey's own fields, empty where not filled in.
export const surveyValues = (j) => ({
  presiteStatus: j?.presiteStatus || '',
  presiteNotes: j?.presiteNotes || '',
  surfaceType: j?.surfaceType || '',
  mstLocation: j?.mstLocation || '',
  permitConditions: j?.permitConditions || '',
  permitLength: j?.permitLength || '',
  plannedDate: j?.plannedDate || '',
});

export const mapsUrl = (j) => {
  const dest = j.lat != null ? `${j.lat},${j.lon}` : encodeURIComponent(`${j.address}, ${j.postcode}`);
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
};
